import type { GetServerSideProps, NextPage } from "next";
import React from "react";
import getAppSettings, { AppSettings } from "../../../lib/get-app-settings";
import authenticateViaUrlParameters from "../../../lib/authenticate-via-url-parameters";
import type {
  HelpCenterLayout,
  THelpCenterLightLayout,
  THelpCenterStandardLayout,
} from "../../../lib/load-company-settings";
import ArticleStandardPage, {
  getServerSideProps as getStandardPageProps,
  type TArticleStandardPageProps,
} from "./_articleSlug.standard";
import ArticleLightPage, {
  getServerSideProps as getLightPageProps,
  type TArticleLightPageProps,
} from "./_articleSlug.light";

type TArticlePageProps = TArticleStandardPageProps | TArticleLightPageProps;

const ArticlePage: NextPage<TArticlePageProps> = ({
  appSettings,
  ...props
}) => {
  if (appSettings.layout.startsWith("light_")) {
    return (
      <ArticleLightPage
        {...(props as TArticleLightPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterLightLayout }
        }
      />
    );
  } else {
    return (
      <ArticleStandardPage
        {...(props as TArticleStandardPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterStandardLayout }
        }
      />
    );
  }
};

export default ArticlePage;

export const getServerSideProps: GetServerSideProps<TArticlePageProps> = async (
  context
) => {
  const appSettings = await getAppSettings(context);

  if (!appSettings) {
    return { redirect: { statusCode: 302, destination: "/" } };
  }

  const authResponse = await authenticateViaUrlParameters(appSettings, context);
  if (authResponse) return authResponse;

  const previewLayout = context.query.preview_layout?.toString();
  if (
    previewLayout?.match(
      /^(light_sidebar|standard_(condensed|horizontal|grid))$/
    )
  ) {
    appSettings.layout = previewLayout as HelpCenterLayout;
  }

  if (appSettings.layout.startsWith("light_")) {
    return getLightPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterLightLayout }
    );
  } else {
    return getStandardPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterStandardLayout }
    );
  }
};
